import { useState } from 'react'

import { useNotification } from '../../../../../../hooks'
import { useDocFlowContext } from '../../../../DocFlow.hooks'
import {
  DocFlowMappingResultTableCell,
  getDocFlowMappingResults,
  updateDocFlowMappingResultItem,
} from '../../../DocFlowMappingResultTable'
import {
  getDocFlowMappingRecipientModalListParams,
  getDocFlowMappingRecipientModalQueueItemAfterSave,
  getDocFlowMappingRecipientModalRecipientId,
  hasDocFlowMappingRecipientModalNext,
  hasDocFlowMappingRecipientModalPrevious,
  isDocFlowMappingRecipientModalRecipientChanged,
  toDocFlowMappingRecipientModalQueueItem,
} from '../../DocFlowMappingRecipientModal.utils'

export function useDocFlowMappingRecipientModalQueue({
  filterValues,
  initialItem,
  initialPosition,
  initialTotal,
  ordering,
  uploadingId,
  onClose,
}) {
  const { documentId } = useDocFlowContext()

  const { handleAddSuccessNotification } = useNotification()

  const [queueItem, setQueueItem] = useState({
    item: initialItem,
    position: initialPosition,
    total: initialTotal,
  })
  const [isLoading, setLoading] = useState(false)
  const [hasChanges, setChanges] = useState(false)
  const [errorKey, setErrorKey] = useState(null)

  const { item, position, total } = queueItem

  const hasNext = hasDocFlowMappingRecipientModalNext(queueItem)
  const hasPrevious = hasDocFlowMappingRecipientModalPrevious(position)

  function handleClose() {
    onClose({ hasChanges })
  }

  function handleLoadError() {
    setErrorKey('Notification.DocumentStaplingMappingResultFileErrorTitleKey')
  }

  function handleSaveError() {
    setErrorKey(
      'Notification.DocumentStaplingMappingResultMassActionErrorTitleKey'
    )
  }

  function applyQueueItem({ isChanged, nextQueueItem }) {
    if (nextQueueItem) {
      setQueueItem(nextQueueItem)
    } else {
      onClose({ hasChanges: isChanged })
    }
  }

  function applyQueueItemAfterSave(queueItemAfterSave) {
    // Inside the pop-up the next document confirms the save, so only the save that closes it gets a toast
    if (!queueItemAfterSave) {
      handleAddSuccessNotification('Notification.CommonTitleSavedKey')
    }

    applyQueueItem({ isChanged: true, nextQueueItem: queueItemAfterSave })
  }

  async function loadQueueItem(queuePosition) {
    return getDocFlowMappingResults({
      data: getDocFlowMappingRecipientModalListParams({
        filterValues,
        ordering,
        position: queuePosition,
      }),
      documentId,
      itemId: uploadingId,
    }).then(({ data }) =>
      toDocFlowMappingRecipientModalQueueItem({ data, position: queuePosition })
    )
  }

  async function loadNextQueueItem(currentQueueItem) {
    return hasDocFlowMappingRecipientModalNext(currentQueueItem)
      ? loadQueueItem(currentQueueItem.position + 1)
      : null
  }

  async function loadQueueItemAfterSave(nextQueueItem) {
    return nextQueueItem
      ? loadQueueItem(position).then(currentQueueItem =>
          getDocFlowMappingRecipientModalQueueItemAfterSave({
            currentQueueItem,
            nextQueueItem,
          })
        )
      : null
  }

  async function showPosition(nextPosition) {
    setErrorKey(null)
    setLoading(true)

    return loadQueueItem(nextPosition)
      .then(nextQueueItem =>
        applyQueueItem({ isChanged: hasChanges, nextQueueItem })
      )
      .catch(handleLoadError)
      .finally(() => {
        setLoading(false)
      })
  }

  async function handleNext() {
    return showPosition(position + 1)
  }

  async function handlePrevious() {
    return showPosition(position - 1)
  }

  async function handleSaved(nextQueueItem) {
    setChanges(true)

    return loadQueueItemAfterSave(nextQueueItem)
      .then(applyQueueItemAfterSave)
      .catch(handleLoadError)
  }

  async function saveRecipient({ nextQueueItem, values }) {
    return updateDocFlowMappingResultItem({
      data: {
        [DocFlowMappingResultTableCell.Recipient]:
          getDocFlowMappingRecipientModalRecipientId(values),
      },
      documentId,
      itemId: uploadingId,
      resultItemId: item.id,
    }).then(() => handleSaved(nextQueueItem))
  }

  async function handleSubmit(values) {
    if (!isDocFlowMappingRecipientModalRecipientChanged({ item, values })) {
      return hasNext ? handleNext() : handleClose()
    }

    setErrorKey(null)
    setLoading(true)

    // A save can move the document in a list ordered by recipient or drop it from the list, so its next one is read first
    return loadNextQueueItem(queueItem)
      .then(nextQueueItem => saveRecipient({ nextQueueItem, values }))
      .catch(handleSaveError)
      .finally(() => {
        setLoading(false)
      })
  }

  return {
    errorKey,
    handleClose,
    handleNext,
    handlePrevious,
    handleSubmit,
    hasNext,
    hasPrevious,
    isLoading,
    item,
    position,
    total,
  }
}
